import { useMemo } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui';
import {
  DEFAULT_VISIBLE_COLUMNS,
  TOGGLEABLE_PRODUCT_COLUMNS,
  useProductColumnPrefs,
  type ToggleableProductColumn,
} from '@/stores/useProductColumnPrefs';

/**
 * 컬럼 id → i18n 라벨 키. totalStockQty·isSoldOut 은 헤더에서 stockQty·stockState 키를
 * 쓰므로(ProductTable) 메뉴 라벨도 동일 키로 맞춰 헤더와 표기를 일치시킨다.
 */
const COLUMN_LABEL_KEY: Record<ToggleableProductColumn, string> = {
  optionCount: 'products.columns.optionCount',
  productType: 'products.columns.productType',
  artistName: 'products.columns.artistName',
  productCode: 'products.columns.productCode',
  barcode: 'products.columns.barcode',
  displayed: 'products.columns.displayed',
  saleStatus: 'products.columns.saleStatus',
  listPrice: 'products.columns.listPrice',
  salePrice: 'products.columns.salePrice',
  releaseDate: 'products.columns.releaseDate',
  createdAt: 'products.columns.createdUpdated',
  totalStockQty: 'products.columns.stockQty',
  isSoldOut: 'products.columns.stockState',
  // QA-R6 H: 행 우측 '상품 복사' 컬럼도 노출 항목 토글 대상.
  copy: 'products.columns.copy',
};

/**
 * ID89(15-B-2) 노출 항목 설정.
 *
 * 상품 목록 상단의 드롭다운에서 운영자가 표시할 컬럼을 체크박스로 켜고 끈다(고도몰 상품
 * 리스트 관용). 선택은 클라이언트 환경설정(localStorage)으로 보관되며 새로고침 후에도 유지.
 * orders 의 CheckboxFilterSelect 와 동일한 Radix DropdownMenu + CheckboxItem 패턴을
 * 따르되, 컬럼 가시성 도메인이라 catalog feature 내에 별도 컴포넌트로 둔다.
 */
export const ProductColumnSettings = () => {
  const { t } = useTranslation('products');
  const visibleColumns = useProductColumnPrefs((s) => s.visibleColumns);
  const toggleColumn = useProductColumnPrefs((s) => s.toggleColumn);
  const resetColumns = useProductColumnPrefs((s) => s.resetColumns);

  const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  // 현재 노출 컬럼이 기본값과 동일한지(같으면 "기본값으로" 버튼 비활성).
  const isDefault = useMemo(() => {
    if (visibleColumns.length !== DEFAULT_VISIBLE_COLUMNS.length) return false;
    const set = new Set(visibleColumns);
    return DEFAULT_VISIBLE_COLUMNS.every((c) => set.has(c));
  }, [visibleColumns]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" data-testid="product-column-settings-button">
          <SlidersHorizontal size={14} aria-hidden="true" />
          {t('products.columnSettings.button', { defaultValue: '노출 항목' })}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel>
          {t('products.columnSettings.title', { defaultValue: '노출 항목 설정' })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TOGGLEABLE_PRODUCT_COLUMNS.map((column) => {
          const checked = visibleSet.has(column);
          // 마지막 남은 1개 컬럼은 끌 수 없게(빈 표 방지) 체크박스를 비활성.
          const isLastVisible = checked && visibleColumns.length <= 1;
          return (
            <DropdownMenuCheckboxItem
              key={column}
              checked={checked}
              disabled={isLastVisible}
              // 여러 항목을 한 번에 토글할 수 있게 선택 후에도 메뉴를 닫지 않는다.
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => toggleColumn(column)}
            >
              {t(COLUMN_LABEL_KEY[column])}
            </DropdownMenuCheckboxItem>
          );
        })}
        <DropdownMenuSeparator />
        <button
          type="button"
          onClick={resetColumns}
          disabled={isDefault}
          className="w-full rounded-sm px-2 py-1.5 text-left text-xs underline disabled:no-underline disabled:opacity-50"
          style={{ color: 'var(--content-muted)' }}
          data-testid="product-column-settings-reset"
        >
          {t('products.columnSettings.reset', { defaultValue: '기본값으로' })}
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
